/**
 * ColumnSettings Component
 *
 * Two-column layout: left category nav, right search + column list. Token-only styling for light/dark.
 */

import React, { useState, useMemo, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { X, GripVertical, Settings } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ColumnState } from './types'
import { ACTIVITY_COLUMNS } from '@/types'

const getAllColumns = (
  columnState: ColumnState[],
  activities?: any[],
  udfLabels?: Record<string, string>
) => {
  const predefinedKeys = new Set(ACTIVITY_COLUMNS.map((col) => col.key))
  const udfColumnsFromState = columnState
    .filter((col) => {
      const keyLower = String(col.key).toLowerCase()
      return (
        (keyLower.startsWith('udf_') || (keyLower.startsWith('udf') && keyLower !== 'udf_values')) &&
        keyLower !== 'udf_values' &&
        !predefinedKeys.has(col.key)
      )
    })
    .map((col) => {
      const udfTypeId = String(col.key).replace(/^udf_?/i, '')
      const displayLabel =
        udfLabels?.[udfTypeId] ? `${udfLabels[udfTypeId]} (UDF)` : udfTypeId ? `UDF ${udfTypeId}` : String(col.key)
      return {
        key: col.key,
        label: displayLabel,
        description: `User Defined Field: ${String(col.key)}`,
        category: 'udf' as const,
        defaultVisible: false,
        sortable: true,
      }
    })

  const udfColumnsFromActivities: typeof ACTIVITY_COLUMNS = []
  if (activities?.length) {
    const detectedUdfKeys = new Set<string>()
    activities.forEach((activity) => {
      Object.keys(activity).forEach((key) => {
        const keyLower = key.toLowerCase()
        if (
          (keyLower.startsWith('udf_') || (keyLower.startsWith('udf') && keyLower !== 'udf_values')) &&
          !predefinedKeys.has(key as any) &&
          !udfColumnsFromState.some((col) => col.key === key) &&
          keyLower !== 'udf_values'
        ) {
          detectedUdfKeys.add(key)
        }
      })
    })
    detectedUdfKeys.forEach((key) => {
      const udfTypeId = key.replace(/^udf_?/i, '')
      const displayLabel =
        udfLabels?.[udfTypeId] ? `${udfLabels[udfTypeId]} (UDF)` : udfTypeId ? `UDF ${udfTypeId}` : key
      udfColumnsFromActivities.push({
        key: key as any,
        label: displayLabel,
        description: `User Defined Field: ${key}`,
        category: 'udf' as const,
        defaultVisible: false,
        sortable: true,
        width: '150px',
      })
    })
  }
  return [...ACTIVITY_COLUMNS, ...udfColumnsFromState, ...udfColumnsFromActivities]
}

interface ColumnSettingsProps {
  columnState: ColumnState[]
  onColumnStateChange: (newState: ColumnState[]) => void
  onClose: () => void
  activities?: any[]
  udfLabels?: Record<string, string>
}

type Category = 'basic' | 'dates' | 'progress' | 'advanced' | 'udf'

const CATEGORIES: { key: Category; label: string }[] = [
  { key: 'basic', label: 'Basic' },
  { key: 'dates', label: 'Dates' },
  { key: 'progress', label: 'Progress' },
  { key: 'advanced', label: 'Advanced' },
  { key: 'udf', label: 'UDF' },
]

export default function ColumnSettings({
  columnState,
  onColumnStateChange,
  onClose,
  activities = [],
  udfLabels = {},
}: ColumnSettingsProps) {
  const [localState, setLocalState] = useState<ColumnState[]>(() =>
    columnState.map((col) => (col.key === 'id' ? { ...col, visible: true } : col))
  )
  const [searchTerm, setSearchTerm] = useState('')
  const [activeCategory, setActiveCategory] = useState<Category>('basic')

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [onClose])

  const handleToggleVisibility = (key: string) => {
    if (key === 'id') return // Activity ID is persistent and cannot be hidden
    setLocalState((prev) => {
      const existingCol = prev.find((col) => String(col.key) === String(key))
      if (existingCol) {
        return prev.map((col) =>
          String(col.key) === String(key) ? { ...col, visible: !col.visible } : col
        )
      }
      return [
        ...prev,
        { key: key as any, visible: true, order: prev.length, decimalsEnabled: false, decimalPlaces: 2 },
      ]
    })
  }

  const handleToggleDecimals = (key: string) => {
    setLocalState((prev) =>
      prev.map((col) =>
        String(col.key) === String(key) ? { ...col, decimalsEnabled: !col.decimalsEnabled } : col
      )
    )
  }

  const handleDecimalPlacesChange = (key: string, places: number) => {
    setLocalState((prev) =>
      prev.map((col) => (String(col.key) === String(key) ? { ...col, decimalPlaces: places } : col))
    )
  }

  const handleMoveUp = (key: string) => {
    setLocalState((prev) => {
      const newState = [...prev]
      const index = newState.findIndex((col) => String(col.key) === String(key))
      if (index > 0) {
        [newState[index], newState[index - 1]] = [newState[index - 1], newState[index]]
        newState.forEach((col, i) => {
          col.order = i
        })
      }
      return newState
    })
  }

  const handleMoveDown = (key: string) => {
    setLocalState((prev) => {
      const newState = [...prev]
      const index = newState.findIndex((col) => String(col.key) === String(key))
      if (index < newState.length - 1) {
        [newState[index], newState[index + 1]] = [newState[index + 1], newState[index]]
        newState.forEach((col, i) => {
          col.order = i
        })
      }
      return newState
    })
  }

  const handleSave = () => {
    const stateToSave = localState.map((col) =>
      col.key === 'id' ? { ...col, visible: true } : col
    )
    onColumnStateChange(stateToSave)
    onClose()
  }

  const handleReset = () => {
    setLocalState(
      ACTIVITY_COLUMNS.map((col, index) => ({
        key: String(col.key),
        visible: col.defaultVisible,
        order: index,
        decimalsEnabled: false,
        decimalPlaces: 2,
      }))
    )
  }

  const allColumnsList = useMemo(
    () => getAllColumns(localState, activities, udfLabels),
    [localState, activities, udfLabels]
  )

  const columnsByCategory = useMemo(() => {
    const allColumnsToShow = new Set<string>()
    localState.forEach((col) => allColumnsToShow.add(String(col.key)))
    allColumnsList.forEach((col) => {
      if (!localState.some((c) => String(c.key) === String(col.key))) {
        allColumnsToShow.add(String(col.key))
      }
    })

    const filtered = Array.from(allColumnsToShow)
      .map((key) => {
        return (
          localState.find((col) => String(col.key) === String(key)) || {
            key,
            visible: false,
            order: localState.length,
            decimalsEnabled: false,
            decimalPlaces: 2,
          }
        )
      })
      .filter((col) => {
        if (!searchTerm) return true
        const columnDef = allColumnsList.find((c) => String(c.key) === String(col.key))
        return (
          String(col.key).toLowerCase().includes(searchTerm.toLowerCase()) ||
          columnDef?.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
          columnDef?.description?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      })

    const grouped: Record<Category, typeof filtered> = {
      basic: [],
      dates: [],
      progress: [],
      advanced: [],
      udf: [],
    }
    filtered.forEach((col) => {
      const columnDef = allColumnsList.find((c) => c.key === col.key)
      const category = columnDef ? (columnDef.category as Category) : null
      const keyLower = String(col.key).toLowerCase()
      const isUdf =
        (keyLower.startsWith('udf_') || (keyLower.startsWith('udf') && keyLower !== 'udf_values')) &&
        keyLower !== 'udf_values'
      const target = category && grouped[category] ? category : isUdf ? 'udf' : 'advanced'
      grouped[target].push(col)
    })
    return grouped
  }, [localState, searchTerm, allColumnsList])

  const currentColumns = columnsByCategory[activeCategory] || []
  const categoryLabels: Record<Category, string> = {
    basic: 'Basic',
    dates: 'Dates',
    progress: 'Progress',
    advanced: 'Advanced',
    udf: 'UDF',
  }

  const rowClass =
    'flex items-center gap-3 rounded-lg border border-border bg-card p-3 hover:bg-accent/30 transition-colors'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="flex max-h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex flex-shrink-0 items-center justify-between border-b border-border bg-muted/50 px-6 py-3">
          <h2 className="flex items-center gap-2.5 text-base font-display font-semibold text-foreground">
            <span className="rounded-md bg-primary/10 p-1.5">
              <Settings className="h-4 w-4 text-primary" />
            </span>
            Column Settings
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Body: two-column */}
        <div className="flex min-h-0 flex-1">
          {/* Left: category nav */}
          <nav className="w-[180px] flex-shrink-0 border-r border-border bg-muted/30 py-3">
            <ul className="space-y-0.5 px-2">
              {CATEGORIES.map(({ key, label }) => (
                <li key={key}>
                  <button
                    type="button"
                    onClick={() => setActiveCategory(key)}
                    className={`w-full rounded-md px-3 py-2 text-left text-sm font-medium transition-colors ${
                      activeCategory === key
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                    }`}
                  >
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          {/* Right: search + column list */}
          <div className="flex min-w-0 flex-1 flex-col overflow-hidden p-5">
            <div className="flex flex-shrink-0 items-center gap-3 pb-3">
              <Input
                placeholder="Search columns..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 h-8 text-sm"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReset}
                className="text-muted-foreground shrink-0"
              >
                Reset to Default
              </Button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              {currentColumns.length === 0 ? (
                <div className="py-12 text-center text-sm text-muted-foreground">
                  {searchTerm
                    ? 'No columns found matching your search'
                    : `No columns in ${categoryLabels[activeCategory]} category`}
                </div>
              ) : (
                <div className="space-y-1.5">
                  {currentColumns.map((col) => {
                    const columnDef = allColumnsList.find((c) => String(c.key) === String(col.key))
                    const label =
                      columnDef?.label || String(col.key).replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
                    const showDecimals =
                      String(col.key).includes('_pct') ||
                      String(col.key) === 'total_float_hr_cnt' ||
                      String(col.key).includes('cost') ||
                      String(col.key).includes('qty') ||
                      String(col.key).includes('duration')

                    return (
                      <div key={String(col.key)} className={rowClass}>
                        <div className="flex items-center gap-1">
                          <GripVertical className="h-3.5 w-3.5 text-muted-foreground/50" />
                          <div className="flex flex-col gap-0">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMoveUp(String(col.key))}
                              disabled={col.order === 0}
                              className="h-5 w-5 p-0 text-muted-foreground hover:text-foreground disabled:opacity-30"
                            >
                              ↑
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMoveDown(String(col.key))}
                              disabled={col.order === localState.length - 1}
                              className="h-5 w-5 p-0 text-muted-foreground hover:text-foreground disabled:opacity-30"
                            >
                              ↓
                            </Button>
                          </div>
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-foreground">{label}</div>
                          <div className="text-xs text-muted-foreground font-mono">{String(col.key)}</div>
                          {columnDef?.description && (
                            <div className="mt-0.5 text-xs text-muted-foreground">{columnDef.description}</div>
                          )}
                        </div>

                        <div className="flex items-center gap-4 shrink-0">
                          <label className={cn('flex items-center gap-2', col.key === 'id' && 'cursor-default')}>
                            <Checkbox
                              checked={col.visible}
                              onCheckedChange={() => col.key !== 'id' && handleToggleVisibility(String(col.key))}
                              disabled={col.key === 'id'}
                            />
                            <span className="text-xs text-muted-foreground">
                              {col.key === 'id' ? 'Always visible' : 'Visible'}
                            </span>
                          </label>

                          {showDecimals && (
                            <div className="flex items-center gap-2">
                              <label className="flex cursor-pointer items-center gap-2">
                                <Checkbox
                                  checked={col.decimalsEnabled}
                                  onCheckedChange={() => handleToggleDecimals(String(col.key))}
                                />
                                <span className="text-xs text-muted-foreground">Decimals</span>
                              </label>
                              {col.decimalsEnabled && (
                                <Input
                                  type="number"
                                  min={0}
                                  max={4}
                                  value={col.decimalPlaces}
                                  onChange={(e) =>
                                    handleDecimalPlacesChange(String(col.key), parseInt(e.target.value, 10) || 0)
                                  }
                                  className="h-7 w-14 text-xs"
                                />
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-shrink-0 justify-end gap-2 border-t border-border bg-muted/50 px-6 py-3">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave}>
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  )
}
